import { useEffect, useState, useRef, useCallback } from 'react';
import { Conversation, ConversationContent, ConversationScrollButton } from '@/components/ai-elements/conversation';
import { Message, MessageContent } from '@/components/ai-elements/message';
import RukiaChatInput from '../../components/sections/RukiaChatInput';
import MarkdownRenderer from '@/components/markdown/MarkdownRenderer';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import './chatPage.css';

export default function ChatPage({
    initialMessage,
    showIntroMessage = false,
    introTitle = "Hello! I'm Rukia.",
    introDescription = 'How can I assist you today?',
    apiEndpoint,
    onInitialMessageProcessed,
    getApiPayload,
}) {
    const [isStreaming, setIsStreaming] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const initialMessageProcessedRef = useRef(false); // New ref to track if initial message has been processed
    const abortControllerRef = useRef(null);
    const thinkingStartTimeRef = useRef(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogContent, setDialogContent] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');
    const [isDialogLoading, setIsDialogLoading] = useState(false);

    const handleStop = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    const getSourceUrl = filename => {
        if (filename.startsWith('http')) return filename;
        if (filename.endsWith('.md')) {
            return `https://lessons.zinduaschool.com/${filename}`;
        }
        return `https://resources.zinduaschool.com/${filename}`;
    };

    const handleSourceClick = async (e, source) => {
        if (source.filename.endsWith('.md')) {
            e.preventDefault();
            setDialogTitle(source.attributes.filename);
            setDialogContent('');
            setIsDialogLoading(true);
            setDialogOpen(true);
            try {
                const url = getSourceUrl(source.filename);
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch content');
                }
                const text = await response.text();
                setDialogContent(text);
            } catch (error) {
                console.error(error);
                setDialogContent('Error loading content.');
            } finally {
                setIsDialogLoading(false);
            }
        }
    };

    const streamResponse = useCallback(
        async query => {
            const apiUrl = apiEndpoint || import.meta.env.VITE_CLOUDFLARE_API_URL;

            // Guard against undefined API URL
            if (!apiUrl) {
                console.error('API endpoint is not configured. Please set VITE_CLOUDFLARE_API_URL or pass an apiEndpoint prop.');
                throw new Error('API endpoint is not configured. Please check your environment variables.');
            }

            abortControllerRef.current = new AbortController();

            const body = getApiPayload ? getApiPayload(query) : JSON.stringify({ query });

            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: body,
                    signal: abortControllerRef.current.signal,
                });

                if (!response.ok) {
                    throw new Error(`API error: ${response.status}`);
                }

                return await response.json();
            } catch (error) {
                if (error.name === 'AbortError') {
                    throw new Error('Request was cancelled');
                }
                throw error;
            }
        },
        [apiEndpoint, getApiPayload]
    );

    const simulateStreaming = useCallback(async (text, messageId, thinkingDuration, sources) => {
        const words = text.split(' ');
        let currentContent = '';

        for (let i = 0; i < words.length; i++) {
            if (abortControllerRef.current?.signal.aborted) {
                break;
            }

            currentContent += (i > 0 ? ' ' : '') + words[i];

            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId
                        ? {
                              ...msg,
                              content: currentContent,
                              metadata: {
                                  thinking: false,
                                  thinkingTime: thinkingDuration,
                                  sources: sources,
                              },
                          }
                        : msg
                )
            );

            const delay = 10; // Reduced delay for faster appearance
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }, []);

    const handleSubmit = useCallback(
        async value => {
            if (!value?.trim() || isStreaming) return;

            setIsStreaming(true);
            setInput('');

            const userMessage = {
                id: crypto.randomUUID(),
                role: 'user',
                content: value,
            };

            // Append the user message unless the most recent user message has the same content
            setMessages(prev => {
                const lastUser = [...prev].reverse().find(m => m.role === 'user');
                if (lastUser && lastUser.content?.trim() === value?.trim()) {
                    // duplicate submission detected — don't add another user message
                    return prev;
                }
                return [...prev, userMessage];
            });

            const assistantMessageId = crypto.randomUUID();
            const assistantMessage = {
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                metadata: {
                    thinking: true,
                    thinkingTime: 0,
                    sources: [],
                },
            };

            setMessages(prev => [...prev, assistantMessage]);
            // Avoid adding multiple assistant placeholders in case of duplicate submissions
            setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.role === 'assistant' && lastMsg.metadata?.thinking) {
                    return prev;
                }
                return [...prev, assistantMessage];
            });
            thinkingStartTimeRef.current = Date.now();

            try {
                const data = await streamResponse(value);
                console.log('API Response:', data);

                const thinkingDuration = ((Date.now() - thinkingStartTimeRef.current) / 1000).toFixed(1);

                let responseText;
                let sources = [];

                // Handle /course-companion response
                if (data?.success && data?.result?.response) {
                    responseText = data.result.response || 'No response available';
                    if (data.result.data && Array.isArray(data.result.data)) {
                        sources = data.result.data.map(source => ({
                            filename: source.filename,
                            attributes: {
                                filename: source.attributes?.filename || source.filename.split('/').pop(),
                            },
                        }));
                    }
                } // Handle /teaching-assistant response
                else if (data?.answer) {
                    responseText = data.answer;
                    if (data.sources && Array.isArray(data.sources)) {
                        sources = data.sources.map(source => ({
                            filename: source.metadata?.source || 'source',
                            attributes: {
                                filename: (source.metadata?.source || 'source').split('/').pop(),
                            },
                        }));
                    }
                } else {
                    // Fallback to the original response structure
                    responseText = data?.response || 'No response available';
                    if (data?.lesson_url) {
                        const filename = data.lesson_url.split('/').pop();
                        sources.push({
                            filename: data.lesson_url,
                            attributes: { filename: filename },
                        });
                    }
                }

                await simulateStreaming(responseText, assistantMessageId, thinkingDuration, sources);
            } catch (error) {
                // Don't log cancellation errors to the console
                if (error.message !== 'Request was cancelled') {
                    console.error('Error streaming response:', error);
                }
                // If the request was cancelled, remove the assistant placeholder message
                if (error.message === 'Request was cancelled') {
                    setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
                } else {
                    let errorMessage = 'Sorry, I encountered an error. Please try again.';
                    if (error.message.includes('API endpoint is not configured')) {
                        errorMessage = 'The chat service is not configured correctly. Please contact support.';
                    } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
                        errorMessage = `Network error. This could be a connection issue or a cross-origin (CORS) problem. Please check your connection and the server configuration.`;
                        console.error(
                            "A 'Failed to fetch' error occurred. This is often a CORS issue. Ensure the server at the API endpoint is configured to accept requests from this origin."
                        );
                    }

                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === assistantMessageId
                                ? {
                                      ...msg,
                                      content: errorMessage,
                                      metadata: {
                                          thinking: false,
                                          thinkingTime: 0,
                                          sources: [],
                                      },
                                  }
                                : msg
                        )
                    );
                }
            } finally {
                setIsStreaming(false);
                abortControllerRef.current = null;
                thinkingStartTimeRef.current = null;
            }
        },
        [isStreaming, streamResponse, simulateStreaming]
    );

    useEffect(() => {
        // Only process the initial message if it exists, hasn't been processed yet,
        // and no other messages are in the conversation (to avoid re-processing on re-renders)
        if (initialMessage && !initialMessageProcessedRef.current && messages.length === 0) {
            (async () => {
                try {
                    // Wait for the submit to start/finish to avoid racing with parent state updates
                    await handleSubmit(initialMessage);
                } catch {
                    // swallow - errors are handled in handleSubmit
                } finally {
                    initialMessageProcessedRef.current = true; // Mark as processed
                    if (onInitialMessageProcessed) {
                        onInitialMessageProcessed(); // Clear initialMessage in parent
                    }
                }
            })();
        }
    }, [
        initialMessage,
        messages.length, // Keep this to re-evaluate if messages are cleared externally
        handleSubmit,
        onInitialMessageProcessed,
        isStreaming, // Add isStreaming to prevent re-triggering if a stream is already active
    ]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const hasMessages = messages.length > 0;

    return (
        <div className="flex flex-col w-full h-full">
            {/* Messages container - centers when no messages, scrolls when messages appear */}
            {hasMessages ? (
                <>
                    <Conversation>
                        <ConversationContent>
                            {messages.map(message => (
                                <Message key={message.id} from={message.role === 'user' ? 'user' : 'assistant'}>
                                    <MessageContent>
                                        {message.role === 'user' ? (
                                            <div className="font-semibold">{message.content}</div>
                                        ) : (
                                            <>
                                                {message.metadata?.thinking ? (
                                                    <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                                                        <span>Looking for the best response</span>
                                                        <div className="flex gap-1">
                                                            <div className="thinking-dot" />
                                                            <div className="thinking-dot" />
                                                            <div className="thinking-dot" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <MarkdownRenderer content={message.content} small={true} />
                                                        {message.metadata?.thinkingTime > 0 && message.content && (
                                                            <div className="text-neutral-400 text-xs mt-2">
                                                                Time Taken: {message.metadata.thinkingTime}ms
                                                            </div>
                                                        )}
                                                        {message.metadata?.sources && message.metadata.sources.length > 0 && (
                                                            <div className="mt-3 text-sm text-neutral-500">
                                                                <span>
                                                                    Used {message.metadata.sources.length}
                                                                    {message.metadata.sources.length > 1 ? 's' : ''}:
                                                                </span>
                                                                {message.metadata.sources.map((source, index) => (
                                                                    <span key={index} className="inline">
                                                                        <a
                                                                            href={getSourceUrl(source.filename)}
                                                                            onClick={e => handleSourceClick(e, source)}
                                                                            rel="noopener noreferrer"
                                                                            target="_blank"
                                                                            className="text-primary font-semibold hover:underline cursor-pointer"
                                                                        >
                                                                            {source.attributes.filename}
                                                                        </a>
                                                                        {index < message.metadata.sources.length - 1 ? ', ' : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </MessageContent>
                                </Message>
                            ))}
                        </ConversationContent>
                        <ConversationScrollButton />
                    </Conversation>
                </>
            ) : (
                <div className="flex-1 flex items-center justify-center px-6 py-12">
                    {showIntroMessage && (
                        <div className="w-full max-w-3xl">
                            {/* Intro Header */}
                            <div className="mb-12">
                                <h4 className="font-semibold text-3xl mb-4">
                                    {introTitle.includes('Rukia') ? (
                                        <>
                                            {introTitle.split('Rukia')[0]}
                                            <span className="text-primary">Rukia</span>
                                            {introTitle.split('Rukia')[1]}
                                        </>
                                    ) : (
                                        introTitle
                                    )}
                                </h4>
                                <p className="text-neutral-600 text-base leading-relaxed">{introDescription}</p>
                            </div>

                            {/* Input and Suggestions */}
                            <div className="flex flex-col gap-8 items-center">
                                {/* Input */}
                                <div className="w-full">
                                    <RukiaChatInput
                                        value={input}
                                        onChange={e => setInput(e.target.value)}
                                        onSubmit={handleSubmit}
                                        onStop={handleStop}
                                        disabled={isStreaming}
                                    />
                                </div>

                                {/* Suggestion Buttons */}
                                <div className="flex flex-wrap gap-3 justify-center">
                                    <Button
                                        variant="outline"
                                        className="rounded-full text-sm text-neutral-600 px-4 py-2 border-neutral-300"
                                    >
                                        Explain this...
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="rounded-full text-sm text-neutral-600 px-4 py-2 border-neutral-300"
                                    >
                                        Generate study plan on...
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="rounded-full text-sm text-neutral-600 px-4 py-2 border-neutral-300"
                                    >
                                        How do I do...
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen} className="p-4">
                <DialogContent className="sm:max-w-200 h-[80vh] flex flex-col p-6 border border-neutral-300">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto grow pr-4">
                        {isDialogLoading ? <p>Loading...</p> : <MarkdownRenderer content={dialogContent} small={true} />}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Input container - only show when there are messages */}
            {hasMessages && (
                <div className="px-4 pb-4 bg-neutral-50">
                    <RukiaChatInput
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onSubmit={handleSubmit}
                        onStop={handleStop}
                        disabled={isStreaming}
                    />
                </div>
            )}
        </div>
    );
}
